// @soft-depth
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import SoftDepthScope from '../../design-system/SoftDepthScope';
import {
  COVER_ANIMALS,
  PERSONA_OPTIONS,
  roleOption,
  coverQuizSignals,
  coverReasonMessageId,
  knowledgeBadge
} from './coverQuizData';
import { discName, isDiscLetter } from '../../constants/disc';
import { DEMO_INFLATION, DEMO_RETURN, formatMonthly, formatWan, planGoals } from './coverGoalData';
import { coverClientReport } from './coverQuizReport';
import { applyImports, coverQuizImports } from './coverQuizImport';
import type { PromptInput } from './coverQuizImport';
import type { DiscLetter } from './coverQuizData';
import { coverQuizPartsDone } from './coverQuizStorage';
import type { CoverQuizResult } from './coverQuizStorage';
import {
  hasServicePrefs,
  sameServicePrefs,
  servicePrefsOfAnswers,
  servicePrefsSummary,
  useServicePrefs,
  writeServicePrefs
} from './coverServicePrefs';
import { useCanEditClientNotes } from '../Advisor/ClientV2/clientNotesStore';
import { pickCopy } from '../../utils/localeCopy';
import { quizLangOf } from './quizLang';

/**
 * What a finished 保障小測驗 tells the adviser, in the order they use it before
 * a meeting: who the person is (DISC), where to start (what they got wrong,
 * what they worry about), what may push back, and how to behave in the room.
 *
 * Shared by the quiz dialog and Smart IDEA's 溝通方式 tab. Read-only except for
 * two doors, each performed by the host because each host saves its own way:
 * copying the inferred DISC letter into the factsheet (`onApplyDisc`), and
 * 匯入客戶資料 — the answers the adviser ticks, written to the A.I. profile
 * (`onImport`, see ./coverQuizImport.ts), and 服務方式, written to the client's
 * notes so the drawer and 重點跟進 can show it at the next follow-up
 * (./coverServicePrefs.ts — this one needs `clientId`, not a host callback).
 *
 * Soft Depth, business mode (the same blue in 招募). The quiz dialog that also shows
 * it is still legacy chrome around this themed body.
 */

type IntlLike = {
  formatMessage: (descriptor: { id: string }, values?: Record<string, unknown>) => string;
  locale?: string;
};

// English shows money in millions (HK$1.2M) where Chinese counts in 萬.
const millions = (value: number) => `HK$${(Math.round(value / 100000) / 10).toLocaleString()}M`;

// The panel's own words. The quiz content it quotes (personas, statements,
// goals, cautions, topics) is the client's page copy and stays as written.
const UI_COPY = {
  'zh-Hant-HK': {
    completedAt: (time: string) => `完成於 ${time}`,
    discRead: '性格推斷',
    onFile: '客戶資料：',
    notSet: '未設定',
    agrees: ' ✓ 一致',
    applyDisc: '套用到性格類型',
    importTitle: '📥 匯入客戶資料',
    importPill: 'A.I. 分析會用到',
    overwrites: '會取代現有資料',
    serviceImport: '服務方式（跟進時提示你）',
    importChosen: (n: number) => `匯入所選（${n}）`,
    reportTitle: '客戶看到的報告',
    reportPill: '見面時可沿用同一說法',
    quote: (text: string) => `「${text}」`,
    trueFalse: '真假挑戰',
    correct: (score: number, total: number) => `答對 ${score} / ${total}`,
    wrong: (statement: string) => `答錯：「${statement}」`,
    opener: '會面切入點：',
    allCorrect: '全部答對，保障觀念清晰，可以直接談方案。',
    goals: '理財目標',
    coverage: (pct: number) => `按現時儲蓄約達 ${pct}%`,
    goalLine: (years: number, low: number, high: number, mLow: string, mHigh: string) =>
      `${years} 年後約 ${formatWan(low, 'zh-Hant-HK')}–${formatWan(
        high,
        'zh-Hant-HK'
      )} · 每月需存約 HK$ ${mLow}–${mHigh}`,
    demoNote: (inflation: number, ret: number) =>
      `示範數字；假設通脹 ${inflation}%、回報 ${ret}%。可作為會面時的理財規劃切入點。`,
    service: '客戶希望的服務方式',
    serviceSaved: '✓ 已記錄，跟進時會提示',
    colon: '：',
    interests: '最想了解的保障',
    objections: '可能會提的顧慮',
    cautions: '溝通提醒',
    topics: '開場話題'
  },
  zh: {
    completedAt: (time: string) => `完成于 ${time}`,
    discRead: '性格推断',
    onFile: '客户资料：',
    notSet: '未设定',
    agrees: ' ✓ 一致',
    applyDisc: '套用到性格类型',
    importTitle: '📥 导入客户资料',
    importPill: 'A.I. 分析会用到',
    overwrites: '会取代现有资料',
    serviceImport: '服务方式（跟进时提示你）',
    importChosen: (n: number) => `导入所选（${n}）`,
    reportTitle: '客户看到的报告',
    reportPill: '见面时可沿用同一说法',
    quote: (text: string) => `「${text}」`,
    trueFalse: '真假挑战',
    correct: (score: number, total: number) => `答对 ${score} / ${total}`,
    wrong: (statement: string) => `答错：「${statement}」`,
    opener: '会面切入点：',
    allCorrect: '全部答对，保障观念清晰，可以直接谈方案。',
    goals: '理财目标',
    coverage: (pct: number) => `按现时储蓄约达 ${pct}%`,
    goalLine: (years: number, low: number, high: number, mLow: string, mHigh: string) =>
      `${years} 年后约 ${formatWan(low, 'zh')}–${formatWan(high, 'zh')} · 每月需存约 HK$ ${mLow}–${mHigh}`,
    demoNote: (inflation: number, ret: number) =>
      `示范数字；假设通胀 ${inflation}%、回报 ${ret}%。可作为会面时的理财规划切入点。`,
    service: '客户希望的服务方式',
    serviceSaved: '✓ 已记录，跟进时会提示',
    colon: '：',
    interests: '最想了解的保障',
    objections: '可能会提的顾虑',
    cautions: '沟通提醒',
    topics: '开场话题'
  },
  en: {
    completedAt: (time: string) => `Completed ${time}`,
    discRead: 'Personality read',
    onFile: 'On file: ',
    notSet: 'Not set',
    agrees: ' ✓ Matches',
    applyDisc: 'Apply to personality type',
    importTitle: '📥 Import to client profile',
    importPill: 'Used by A.I. analysis',
    overwrites: 'Replaces what is on file',
    serviceImport: 'Service style (reminded at follow-up)',
    importChosen: (n: number) => `Import selected (${n})`,
    reportTitle: 'Report the client sees',
    reportPill: 'Use the same wording when you meet',
    quote: (text: string) => `"${text}"`,
    trueFalse: 'True or false challenge',
    correct: (score: number, total: number) => `${score} / ${total} correct`,
    wrong: (statement: string) => `Got wrong: "${statement}"`,
    opener: 'Meeting opener: ',
    allCorrect: 'All correct: they understand protection well, so you can go straight to solutions.',
    goals: 'Financial goals',
    coverage: (pct: number) => `About ${pct}% covered by current savings`,
    goalLine: (years: number, low: number, high: number, mLow: string, mHigh: string) =>
      `In ${years} years: about ${millions(low)}–${millions(high)} · save about HK$ ${mLow}–${mHigh} a month`,
    demoNote: (inflation: number, ret: number) =>
      `Illustrative figures, assuming ${inflation}% inflation and ${ret}% return. A starting point for planning at the meeting.`,
    service: "Client's preferred service style",
    serviceSaved: '✓ Saved, you will be reminded at follow-up',
    colon: ': ',
    interests: 'Cover they most want to learn about',
    objections: 'Concerns they may raise',
    cautions: 'Communication tips',
    topics: 'Conversation starters'
  }
};

export interface CoverQuizInsightsProps {
  result: CoverQuizResult;
  /** For the objection names and reasons, which are Smart IDEA's localised copy. */
  intl: IntlLike;
  /** The DISC letter on file, to show whether the quiz agrees with it. */
  currentDisc?: string | null;
  onApplyDisc?: (letter: DiscLetter) => void;
  /** `factsheet.promptInput` on file, so the import offers only what is new. */
  currentPromptInput?: PromptInput | null;
  /** Saves the merged `promptInput`. Without it the import list is hidden. */
  onImport?: (next: PromptInput) => void;
  /** Whose notes 服務方式 is imported into. Without it (or without edit rights) that row is hidden. */
  clientId?: string | null;
}

const SERVICE_IMPORT = 'servicePrefs';

/** A section heading with an optional pill after it. */
const BlockHead = (props: { children: React.ReactNode; pill?: React.ReactNode; pillTestId?: string }) => (
  <Typography
    component="h4"
    variant="body1"
    sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, m: 0, fontWeight: 700, color: 'grey.800' }}
  >
    {props.children}
    {props.pill && <Chip size="small" label={props.pill} data-testid={props.pillTestId} />}
  </Typography>
);

const Block = (props: { children: React.ReactNode; testId?: string; sx?: SxProps<Theme> }) => (
  <Stack component="section" data-testid={props.testId} sx={[{ gap: 1 }, ...(props.sx ? [props.sx].flat() : [])]}>
    {props.children}
  </Stack>
);

const Muted = (props: { children: React.ReactNode }) => (
  <Typography component="p" variant="body2" sx={{ m: 0, color: 'text.secondary' }}>
    {props.children}
  </Typography>
);

// Chips that may hold a sentence: they wrap instead of ellipsizing.
const WRAP_CHIP = { height: 'auto', minHeight: 28, '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } } as const;

/**
 * A list of short rows. `plain` rows are grey wells; otherwise each row is a
 * guess (可能會提的顧慮) and gets the dashed amber outline that says so.
 */
const RowList = (props: { items: { key: string; content: React.ReactNode }[]; plain?: boolean; ordered?: boolean }) => (
  <Stack component={props.ordered ? 'ol' : 'ul'} sx={{ gap: 0.75, m: 0, p: 0, listStyle: 'none' }}>
    {props.items.map(item => (
      <Typography
        component="li"
        variant="body2"
        key={item.key}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 10px',
          px: 1.5,
          py: 1,
          borderRadius: 1.25,
          color: 'text.primary',
          ...(props.plain
            ? { bgcolor: 'grey.50' }
            : { border: '1px dashed', borderColor: 'warning.border', bgcolor: 'background.paper' })
        }}
      >
        {item.content}
      </Typography>
    ))}
  </Stack>
);

/** One 匯入 row: a native checkbox (the phone's own) and what it would write. */
const ImportOption = (props: {
  field: string;
  checked: boolean;
  onToggle: () => void;
  title: string;
  value: string;
  overwrites?: string;
}) => (
  <li>
    <Box
      component="label"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        px: 1.25,
        py: 1,
        borderRadius: 1.25,
        bgcolor: 'background.paper',
        cursor: 'pointer'
      }}
    >
      <Box
        component="input"
        type="checkbox"
        checked={props.checked}
        data-testid="cover-quiz-import-option"
        data-field={props.field}
        onChange={props.onToggle}
        sx={{
          flexShrink: 0,
          width: 18,
          height: 18,
          m: 0,
          mt: '1px',
          accentColor: (theme: Theme) => theme.palette.primary.main
        }}
      />
      <Stack sx={{ gap: '1px', minWidth: 0 }}>
        <Typography component="strong" variant="body2" sx={{ fontWeight: 700, color: 'grey.800' }}>
          {props.title}
        </Typography>
        <Typography component="span" variant="body2" sx={{ color: 'text.primary' }}>
          {props.value}
        </Typography>
        {props.overwrites && (
          <Typography component="small" variant="caption" sx={{ color: 'warning.dark' }}>
            {props.overwrites}
          </Typography>
        )}
      </Stack>
    </Box>
  </li>
);

const CoverQuizInsights = (props: CoverQuizInsightsProps) => {
  const { result, currentDisc, onApplyDisc, currentPromptInput, onImport, intl, clientId } = props;
  const t = (id: string) => intl.formatMessage({ id });
  const ui = pickCopy(UI_COPY, intl.locale);
  const lang = quizLangOf(intl.locale);
  const signals = coverQuizSignals(result.answers, lang);
  const badge = knowledgeBadge(signals.knowledge.score, signals.knowledge.total);
  const animal = signals.disc ? COVER_ANIMALS[signals.disc] : null;
  const onFile = isDiscLetter(currentDisc) ? currentDisc : null;
  const agrees = Boolean(signals.disc && onFile === signals.disc);
  const partOne = coverQuizPartsDone(result).one;
  const persona = roleOption(signals.role || undefined) || PERSONA_OPTIONS.find(o => o.key === signals.persona);
  const plan = planGoals(result.answers.goals, result.answers.persona, lang);
  // Rebuilt from the answers, so it is what the client's page shows for them today.
  const report = coverClientReport(result.answers, lang);
  const imports = onImport ? coverQuizImports(result.answers, currentPromptInput || {}, lang) : [];
  // 服務方式: offered when the quiz says something the record does not already say.
  const servicePrefsOnFile = useServicePrefs(clientId);
  const canEditNotes = useCanEditClientNotes(clientId);
  const quizServicePrefs = servicePrefsOfAnswers(result.answers);
  const serviceOffer =
    clientId &&
    canEditNotes &&
    hasServicePrefs(quizServicePrefs) &&
    !sameServicePrefs(quizServicePrefs, servicePrefsOnFile)
      ? {
          current: servicePrefsSummary(servicePrefsOnFile, lang),
          next: servicePrefsSummary(quizServicePrefs, lang),
          overwrites: hasServicePrefs(servicePrefsOnFile)
        }
      : null;
  const importKey = [
    ...imports.map(i => `${i.field}:${i.next}`),
    serviceOffer ? `${SERVICE_IMPORT}:${serviceOffer.next}` : ''
  ].join('|');
  const [unticked, setUnticked] = useState<string[]>([]);
  // A fresh offer (new answers, or an import that just landed) starts from the defaults.
  useEffect(
    () =>
      setUnticked([
        ...imports.filter(i => i.overwrites).map(i => i.field as string),
        ...(serviceOffer?.overwrites ? [SERVICE_IMPORT] : [])
      ]),
    [importKey]
  );
  const chosen = imports.filter(i => !unticked.includes(i.field));
  const serviceChosen = Boolean(serviceOffer) && !unticked.includes(SERVICE_IMPORT);
  const chosenCount = chosen.length + (serviceChosen ? 1 : 0);
  const applyChosen = () => {
    if (onImport && chosen.length) onImport(applyImports(currentPromptInput || {}, chosen));
    if (serviceChosen && clientId) writeServicePrefs(clientId, quizServicePrefs);
  };
  const toggleImport = (field: string) =>
    setUnticked(prev => (prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]));

  return (
    <SoftDepthScope mode="business">
      <Stack data-testid="cover-quiz-insights" sx={{ gap: 1.5, color: 'text.primary' }}>
        <Typography component="p" variant="subtitle2" sx={{ m: 0, fontWeight: 500, color: 'text.secondary' }}>
          {ui.completedAt(moment(result.completedAt).format('YYYY-MM-DD HH:mm'))}
          {persona && ` · ${persona.icon} ${persona.label[lang]}`}
        </Typography>

        {animal && signals.disc && (
          // The type in its own hue, as on the persona card and DISC 對話建議.
          <Box
            component="section"
            data-testid="cover-quiz-insights-disc"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
              px: 1.75,
              py: 1.5,
              borderRadius: 1.5,
              bgcolor: animal.bg
            }}
          >
            <Typography component="span" variant="h4" aria-hidden="true">
              {animal.emoji}
            </Typography>
            <Stack component="span" sx={{ flex: '1 1 160px', gap: '1px' }}>
              <Typography component="small" variant="subtitle2" sx={{ fontWeight: 500, color: 'grey.600' }}>
                {ui.discRead}
              </Typography>
              <Typography component="strong" variant="h6" sx={{ color: animal.color }}>
                {animal.name[lang]} ({signals.disc})
              </Typography>
              <Typography component="small" variant="subtitle2" sx={{ fontWeight: 500, color: 'grey.600' }}>
                {ui.onFile}
                {onFile ? `${discName(onFile, intl.locale)} (${onFile})` : ui.notSet}
                {agrees && ui.agrees}
              </Typography>
            </Stack>
            {onApplyDisc && !agrees && (
              <Button
                variant="outlined"
                data-testid="cover-quiz-apply-disc"
                onClick={() => onApplyDisc(signals.disc as DiscLetter)}
                sx={{ bgcolor: 'background.paper' }}
              >
                {ui.applyDisc}
              </Button>
            )}
          </Box>
        )}

        {(imports.length > 0 || serviceOffer) && (
          <Block
            testId="cover-quiz-import"
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'primary.border',
              bgcolor: 'primary.soft'
            }}
          >
            <BlockHead pill={ui.importPill}>{ui.importTitle}</BlockHead>
            <Stack component="ul" sx={{ gap: 0.75, m: 0, p: 0, listStyle: 'none' }}>
              {imports.map(item => (
                <ImportOption
                  key={item.field}
                  field={item.field}
                  checked={!unticked.includes(item.field)}
                  onToggle={() => toggleImport(item.field)}
                  title={item.title}
                  value={item.current && item.overwrites ? `${item.current} → ${item.next}` : item.next}
                  overwrites={item.overwrites ? ui.overwrites : undefined}
                />
              ))}
              {serviceOffer && (
                <ImportOption
                  key={SERVICE_IMPORT}
                  field={SERVICE_IMPORT}
                  checked={!unticked.includes(SERVICE_IMPORT)}
                  onToggle={() => toggleImport(SERVICE_IMPORT)}
                  title={ui.serviceImport}
                  value={
                    serviceOffer.current && serviceOffer.overwrites
                      ? `${serviceOffer.current} → ${serviceOffer.next}`
                      : serviceOffer.next
                  }
                  overwrites={serviceOffer.overwrites ? ui.overwrites : undefined}
                />
              )}
            </Stack>
            <Button
              variant="contained"
              data-testid="cover-quiz-import-apply"
              disabled={!chosenCount}
              onClick={applyChosen}
              sx={{ alignSelf: 'flex-start' }}
            >
              {ui.importChosen(chosenCount)}
            </Button>
          </Block>
        )}

        {(report.priorities.length > 0 || report.tips.length > 0) && (
          <Block testId="cover-quiz-client-report">
            <BlockHead pill={ui.reportPill}>{ui.reportTitle}</BlockHead>
            <Muted>{ui.quote(report.headline)}</Muted>
            {report.priorities.length > 0 && (
              <RowList
                plain
                ordered
                items={report.priorities.map((p, i) => ({
                  key: p.key,
                  content: (
                    <>
                      <strong>
                        {i + 1}. {p.icon} {p.title}
                      </strong>
                      <Box component="span" sx={{ color: 'grey.600' }}>
                        {p.why}
                      </Box>
                    </>
                  )
                }))}
              />
            )}
            {report.tips.length > 0 && (
              <RowList
                plain
                items={report.tips.map(tip => ({
                  key: tip.key,
                  content: (
                    <Box component="span" sx={{ color: 'grey.600' }}>
                      {tip.icon} {tip.text}
                    </Box>
                  )
                }))}
              />
            )}
          </Block>
        )}

        {/* A record holding only 第二部分 (sent on its own) has no 真假挑戰 to read. */}
        {partOne && (
          <Block>
            <BlockHead
              pill={`${badge.emoji} ${ui.correct(signals.knowledge.score, signals.knowledge.total)} · ${
                badge.name[lang]
              }`}
            >
              {ui.trueFalse}
            </BlockHead>
            {signals.knowledge.wrong.length > 0 ? (
              signals.knowledge.wrong.map(q => (
                // A wrong answer is where the meeting starts: a warning-tinted tile.
                <Stack
                  key={q.id}
                  data-testid="cover-quiz-talking-point"
                  sx={{
                    gap: 0.5,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'warning.border',
                    bgcolor: 'warning.soft'
                  }}
                >
                  <Typography component="small" variant="subtitle2" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                    {ui.wrong(q.statement[lang])}
                  </Typography>
                  <Typography component="span" variant="body2" sx={{ color: 'text.primary' }}>
                    <strong>{ui.opener}</strong>
                    {q.talkingPoint[lang]}
                  </Typography>
                </Stack>
              ))
            ) : (
              <Muted>{ui.allCorrect}</Muted>
            )}
          </Block>
        )}

        {plan && (
          <Block testId="cover-quiz-insights-goals">
            <BlockHead pill={plan.coveragePct !== null ? ui.coverage(plan.coveragePct) : undefined}>
              {ui.goals}
            </BlockHead>
            <RowList
              plain
              items={plan.items.map(item => ({
                key: item.id,
                content: (
                  <>
                    <strong>
                      {item.icon} {item.label}
                    </strong>
                    <span>
                      {ui.goalLine(
                        item.years,
                        item.futureLow,
                        item.futureHigh,
                        formatMonthly(item.monthlyLow),
                        formatMonthly(item.monthlyHigh)
                      )}
                    </span>
                  </>
                )
              }))}
            />
            <Muted>{ui.demoNote(Math.round(DEMO_INFLATION * 100), Math.round(DEMO_RETURN * 100))}</Muted>
          </Block>
        )}

        {signals.service.length > 0 && (
          <Block testId="cover-quiz-insights-service">
            <BlockHead
              pill={
                hasServicePrefs(servicePrefsOnFile) && sameServicePrefs(quizServicePrefs, servicePrefsOnFile)
                  ? ui.serviceSaved
                  : undefined
              }
              pillTestId="cover-quiz-service-imported"
            >
              {ui.service}
            </BlockHead>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {signals.service.map(item => (
                <Chip
                  key={item.id}
                  sx={WRAP_CHIP}
                  label={`${item.short[lang]}${ui.colon}${item.option.icon} ${item.option.label[lang]}`}
                />
              ))}
            </Stack>
          </Block>
        )}

        {signals.interests.length > 0 && (
          <Block testId="cover-quiz-interests">
            <BlockHead>{ui.interests}</BlockHead>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {signals.interests.map(o => (
                <Chip key={o.key} color="primary" sx={WRAP_CHIP} label={`${o.icon} ${o.label[lang]}`} />
              ))}
            </Stack>
          </Block>
        )}

        {signals.objections.length > 0 && (
          <Block testId="cover-quiz-objections">
            <BlockHead>{ui.objections}</BlockHead>
            <RowList
              items={signals.objections.map(o => ({
                key: o.id,
                content: (
                  <>
                    <strong>{t(`smart-idea-objection-${o.id}`)}</strong>
                    <Box component="span" sx={{ color: 'warning.dark' }}>
                      {o.reasons.map(coverReasonMessageId).map(t).join(t('smart-idea-objection-why-separator'))}
                    </Box>
                  </>
                )
              }))}
            />
          </Block>
        )}

        {signals.cautions.length > 0 && (
          <Block>
            <BlockHead>{ui.cautions}</BlockHead>
            <RowList plain items={signals.cautions.map(c => ({ key: c, content: c }))} />
          </Block>
        )}

        {signals.topics.length > 0 && (
          <Block>
            <BlockHead>{ui.topics}</BlockHead>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {signals.topics.map(topic => (
                <Chip key={topic} sx={WRAP_CHIP} label={topic} />
              ))}
            </Stack>
          </Block>
        )}
      </Stack>
    </SoftDepthScope>
  );
};

export default CoverQuizInsights;
